import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { IntegratedComponentProvider } from '@components/integrated/shared';

import { extranetApiClient } from './api/extranetApiClient';
import { useExtranetAuthStore } from './auth/store';
import { ExtranetAuthGuard } from './components/ExtranetAuthGuard';
import { ExtranetLayout } from './components/ExtranetLayout';
import { registerExtranetComponents } from './components/register';
import { useApplyBrandTheme } from './hooks/useApplyBrandTheme';
import { EXTRANET_ROUTES, extranetPath } from './navigation';
import { ClaimDetailPage } from './pages/ClaimDetailPage';
import { ClaimsPage } from './pages/ClaimsPage';
import { ContractDetailPage } from './pages/ContractDetailPage';
import { ContractsPage } from './pages/ContractsPage';
import { FleetPage } from './pages/FleetPage';
import { HomePage } from './pages/HomePage';
import { PendingPiecesPage } from './pages/PendingPiecesPage';
import { PremiumsPage } from './pages/PremiumsPage';
import { VehicleDetailPage } from './pages/VehicleDetailPage';

// Register the extranet configurable components with the shared registry
// once, at module import time. Idempotent (HMR / test re-imports are safe).
registerExtranetComponents();

/** Redirect an unknown sub-path to the accueil of the tenant. */
function RedirectHome() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return <Navigate to={extranetPath(tenantSlug)} replace />;
}

/**
 * Extranet module routes, mounted at `/extranet/:tenantSlug/*` (`App.tsx`).
 *
 * The auth guard renders the login page at any URL until the user is
 * authenticated (claimflow pattern). The `IntegratedComponentProvider`
 * (`module="extranet"`, `extranetApiClient`) is what the configurable rules
 * hooks read, so every `extranet.*` component fetches its rules from
 * `/extranet/component-rules` with the extranet session.
 */
export function ExtranetRoutes() {
  const activeTenantId = useExtranetAuthStore((s) => s.activeTenantId);
  useApplyBrandTheme();

  return (
    <ExtranetAuthGuard>
      <IntegratedComponentProvider
        apiClient={extranetApiClient}
        tenant={activeTenantId ?? ''}
        module="extranet"
      >
        <ExtranetLayout>
          <Routes>
            <Route path={EXTRANET_ROUTES.home} element={<HomePage />} />
            <Route path={EXTRANET_ROUTES.contracts} element={<ContractsPage />} />
            <Route path={EXTRANET_ROUTES.contract} element={<ContractDetailPage />} />
            <Route path={EXTRANET_ROUTES.claims} element={<ClaimsPage />} />
            <Route path={EXTRANET_ROUTES.claim} element={<ClaimDetailPage />} />
            <Route path={EXTRANET_ROUTES.premiums} element={<PremiumsPage />} />
            <Route path={EXTRANET_ROUTES.fleet} element={<FleetPage />} />
            <Route path={EXTRANET_ROUTES.vehicle} element={<VehicleDetailPage />} />
            <Route path={EXTRANET_ROUTES.pieces} element={<PendingPiecesPage />} />
            {/* Unknown sub-path → accueil. */}
            <Route path="*" element={<RedirectHome />} />
          </Routes>
        </ExtranetLayout>
      </IntegratedComponentProvider>
    </ExtranetAuthGuard>
  );
}
